import { DefaultMap } from "@glideapps/ts-necessities";
import { isRow } from "../../computation-model/data";

import { SerializedComponent } from "../../components/SerializedApp";
import { getFeatureSetting } from "../../feature-settings";
import { panic } from "../../Support";
import { makeRowIndexOrRowID, makeValueForSourceColumn } from "../builder-utils";
import {
    AllowedDataEdits,
    ComponentBuilder,
    ComponentConfiguratorContext,
    PropertyDescriptor,
    WireActionHydrator,
    WireInflationBackend,
} from "../components";
import { thisItemEnumPropertyDescriptorCase } from "../components/descriptor-utils";
import { ColumnPropertyFlag, ColumnPropertyHandler } from "../components/property-handlers";
import { PropertySection } from "../components/property-sections";
import { makeEnumProperty } from "../components/property-utils";
import {
    ActionDescription,
    ActionKind,
    InputOutputTables,
    PropertyDescription,
    TableGlideType,
    isColumnNonHidden,
    isSingleRelationType,
    getTableColumnDisplayName,
    getTableName,
    MutatingScreenKind,
} from "../description";
import { LetBinding } from "../serialized-component";
import { ActionDescriptor } from "./action-descriptor";
import { DescriptionToken } from "./action-handler";
import { BaseActionHandler } from "./base";
import { getDestinationRowGetter, getOutputColumn, withOutputContextForwardee } from "./set-columns";

export interface DeleteRowActionDescription extends ActionDescription {
    readonly kind: ActionKind.DeleteRow;
    readonly rowToDelete: PropertyDescription;
}

export class DeleteRowActionHandler extends BaseActionHandler<DeleteRowActionDescription> {
    public readonly kind = ActionKind.DeleteRow;
    // FIXME: Proper icon
    public readonly iconName = "minus-circle";

    public getDescriptor(): ActionDescriptor {
        const propertyDescriptors: PropertyDescriptor[] = [
            {
                property: { name: "rowToDelete" },
                label: "Row",
                section: PropertySection.Data,
                cases: [
                    thisItemEnumPropertyDescriptorCase,
                    new ColumnPropertyHandler(
                        "rowToDelete",
                        "Row",
                        [ColumnPropertyFlag.Editable, ColumnPropertyFlag.Required],
                        undefined,
                        undefined,
                        t => t.columns.filter(c => isSingleRelationType(c.type) && isColumnNonHidden(c)),
                        "table-ref",
                        PropertySection.Destination
                    ),
                ],
            },
        ];
        return {
            name: "Delete Row",
            isLegacy: !getFeatureSetting("deleteRowAction"),
            needsScreenContext: true,
            properties: propertyDescriptors,
        };
    }

    public newActionDescription(_tables: InputOutputTables): DeleteRowActionDescription {
        return {
            kind: this.kind,
            rowToDelete: makeEnumProperty(true),
        };
    }

    public makeAction(
        builder: ComponentBuilder,
        desc: DeleteRowActionDescription,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined,
        outputContext: SerializedComponent,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _detailScreenTitle: SerializedComponent | undefined,
        _itemID: SerializedComponent | undefined,
        _onSuccess: SerializedComponent | undefined,
        fromInlineList: boolean
    ): SerializedComponent | undefined {
        const { b } = builder;

        if (screenContext === undefined) {
            return panic("Must have screen context for set columns action");
        }

        return withOutputContextForwardee(b, outputContext, (outputLets, outputForwardee) => {
            const tableAndColumn = getOutputColumn(desc.rowToDelete, tables, builder);
            if (tableAndColumn === undefined) return undefined;

            let outputTable: TableGlideType;

            if (tableAndColumn !== true) {
                const columnValue = makeValueForSourceColumn(
                    builder,
                    tableAndColumn.sourceColumn,
                    outputForwardee,
                    tables.output,
                    undefined,
                    undefined,
                    false
                );
                if (columnValue === undefined) return undefined;

                outputTable = tableAndColumn.table;
                outputForwardee = b.getForwardee(columnValue.value);
            } else {
                outputTable = tables.output;
            }

            const outerLets: LetBinding[] = [["out", outputForwardee]];

            const rowIndexOrRowID = makeRowIndexOrRowID(b, outputTable, undefined, b.getVariable("out"));

            let action = b.deleteRow(getTableName(outputTable), rowIndexOrRowID);
            // If we're deleting "this item" and we're on a detail screen, close
            // the screen.
            if (tableAndColumn === true && !fromInlineList) {
                action = b.sequence([action, b.popAnyScreen]);
            }

            return b.holdAction(b.let([...outputLets, ...outerLets], b.if(b.canWriteRow(rowIndexOrRowID), action)));
        });
    }

    public getAllowedDataEdits(
        desc: DeleteRowActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): AllowedDataEdits | undefined {
        const tableAndColumn = getOutputColumn(desc.rowToDelete, tables, ccc);
        if (tableAndColumn === undefined) return undefined;

        const outputTable = tableAndColumn === true ? tables.output : tableAndColumn.table;

        return {
            addRowToTable: new DefaultMap(() => new Set()),
            setColumnsInRow: new DefaultMap(() => new Set()),
            deleteRow: new Set([outputTable]),
        };
    }

    public getTokenizedDescription(
        desc: DeleteRowActionDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): readonly DescriptionToken[] | undefined {
        const outputColumn = getOutputColumn(desc.rowToDelete, tables, ccc);
        if (outputColumn === undefined) return undefined;
        if (outputColumn === true) return [{ kind: "string", value: "this row" }];
        return [{ kind: "column", value: getTableColumnDisplayName(outputColumn.column) }];
    }

    public inflate(ib: WireInflationBackend, desc: DeleteRowActionDescription): WireActionHydrator | undefined {
        const destination = getDestinationRowGetter(ib, desc.rowToDelete);
        if (destination === undefined) return undefined;
        const { destTable, destRowGetter } = destination;

        return vp => {
            const destRow = destRowGetter(vp);
            if (!isRow(destRow)) return undefined;

            return ab => ab.deleteRow(getTableName(destTable), destRow);
        };
    }
}
